import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://family-tree-api-ejg6cacvgvh6fwbh.southeastasia-01.azurewebsites.net/api';
console.log('API_BASE_URL:', API_BASE_URL);
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const userListCache = { promise: null as Promise<any> | null };
const userDetailCache = new Map<number, Promise<any>>();
const familyNameListCache = { promise: null as Promise<any> | null };

const invalidateUserCaches = () => {
  userListCache.promise = null;
  userDetailCache.clear();
};

const invalidateFamilyNameCaches = () => {
  familyNameListCache.promise = null;
};

// Add token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    const parsedUser = JSON.parse(storedUser);
    if (parsedUser?.id) {
      config.headers['X-User-Id'] = parsedUser.id.toString();
    }
  }

  return config;
});

export const authService = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },
};

export const userService = {
  getAllUsers: async () => {
    if (!userListCache.promise) {
      userListCache.promise = apiClient.get('/users').then((response) => response.data);
    }

    try {
      return await userListCache.promise;
    } finally {
      userListCache.promise = null;
    }
  },

  getUserById: async (id: number) => {
    if (!userDetailCache.has(id)) {
      const request = apiClient.get(`/users/${id}`).then((response) => response.data);
      userDetailCache.set(id, request);
    }

    return userDetailCache.get(id)!;
  },

  createUser: async (userData: {
    fullName: string;
    homeName?: string;
    email: string;
    birthDay?: number | null;
    birthMonth?: number | null;
    birthYear?: number | null;
    diedAt?: string;
    diedYear?: number | null;
    isAlive?: boolean;
    gender: string;
    spouseName?: string;
    spouseImageUrl?: string;
    spouseBirthDay?: number | null;
    spouseBirthMonth?: number | null;
    spouseBirthYear?: number | null;
    spouseDiedAt?: string;
    spouseDiedYear?: number | null;
    spouseIsAlive?: boolean;
    parentId?: number;
    familyNameId: number;
    isFamilyAdmin?: boolean;
  }) => {
    const response = await apiClient.post('/users', userData);
    invalidateUserCaches();
    return response.data;
  },

  updateUser: async (id: number, userData: any) => {
    const response = await apiClient.put(`/users/${id}`, userData);
    invalidateUserCaches();
    return response.data;
  },

  deleteUser: async (id: number) => {
    const response = await apiClient.delete(`/users/${id}`);
    invalidateUserCaches();
    return response.data;
  },

  uploadImage: async (id: number, file: File, isSpouseImage: boolean) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/users/${id}/upload-image?isSpouseImage=${isSpouseImage}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    invalidateUserCaches();
    return response.data;
  },

  changePassword: async (id: number, oldPassword: string, newPassword: string) => {
    const response = await apiClient.post(`/users/${id}/change-password`, {
      oldPassword,
      newPassword,
    });
    invalidateUserCaches();
    return response.data;
  },

  resetPassword: async (id: number) => {
    const response = await apiClient.post(`/users/${id}/reset-password`);
    invalidateUserCaches();
    return response.data;
  },
};

export const familyNameService = {
  getAll: async () => {
    if (!familyNameListCache.promise) {
      familyNameListCache.promise = apiClient.get('/familynames').then((response) => response.data);
    }

    return familyNameListCache.promise;
  },

  getById: async (id: number) => {
    const response = await apiClient.get(`/familynames/${id}`);
    return response.data;
  },

  create: async (data: { name: string; headUserId?: number | null }) => {
    const response = await apiClient.post('/familynames', data);
    invalidateFamilyNameCaches();
    return response.data;
  },

  update: async (id: number, data: { name: string; headUserId?: number | null }) => {
    const response = await apiClient.put(`/familynames/${id}`, data);
    invalidateFamilyNameCaches();
    return response.data;
  },

  delete: async (id: number) => {
    const response = await apiClient.delete(`/familynames/${id}`);
    invalidateFamilyNameCaches();
    return response.data;
  },
};

export const familyTreeService = {
  createShareToken: async (familyNameId: number) => {
    const response = await apiClient.post('/familytree/share-token', { familyNameId });
    return response.data;
  },

  getSharedTree: async (familyNameId: number, token: string) => {
    const response = await apiClient.get('/familytree/shared', {
      params: { familyNameId, token },
    });
    return response.data;
  },
};

export const dataImportExportService = {
  exportData: async () => {
    const response = await apiClient.get('/dataimportexport/export', {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const contentDisposition = response.headers['content-disposition'];
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `family-tree-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.zip`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  },

  importData: async (formData: FormData) => {
    const response = await apiClient.post('/dataimportexport/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default apiClient;
